import { ArrayFieldTemplateProps } from "@rjsf/utils";
import { useState } from "react";

import { CustomUiSchemaOptions } from "../../../form-ui-options";

export const useTemplateArrayField = ({
  items,
  schema,
  uiSchema,
  onAddClick,
}: ArrayFieldTemplateProps) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  // Get the field that should be used for labeling and auto-numbering
  const labelField = (uiSchema as CustomUiSchemaOptions | undefined)?.["ui:options"]?.labelField;

  // Wrapped add handler to set default value for labelField
  const handleAddClick = (event: React.MouseEvent) => {
    if (
      labelField &&
      schema.items &&
      typeof schema.items === "object" &&
      "properties" in schema.items
    ) {
      // Set default value for the label field
      const defaultValue = String(items.length + 1);

      // Store the default in the schema temporarily
      const itemsSchema = schema.items as { properties?: Record<string, unknown> };
      if (itemsSchema.properties && labelField in itemsSchema.properties) {
        const fieldSchema = itemsSchema.properties[labelField] as { default?: unknown };
        const originalDefault = fieldSchema.default;
        fieldSchema.default = defaultValue;

        // Call original handler
        onAddClick(event);

        // Restore original default
        if (originalDefault === undefined) {
          delete fieldSchema.default;
        } else {
          fieldSchema.default = originalDefault;
        }
        return;
      }
    }
    onAddClick(event);
  };

  // Get label value for a specific item
  const getItemLabel = (item: ArrayFieldTemplateProps["items"][number]) => {
    if (!labelField) return `${item.index + 1}`;

    // Access the formData for this item to get the actual value
    const itemData = item.children.props?.formData;
    if (itemData && labelField in itemData && itemData[labelField]) {
      return String(itemData[labelField]);
    }

    return `${item.index + 1}`;
  };

  // Handle active index update when removing items
  const handleRemoveItem = (index: number) => {
    const item = items[index];
    item.onDropIndexClick(item.index)();
    const newLength = items.length - 1;
    if (activeIndex >= newLength) {
      setActiveIndex(Math.max(0, newLength - 1));
    } else if (index < activeIndex) {
      setActiveIndex(activeIndex - 1);
    }
  };

  return {
    activeIndex,
    setActiveIndex,
    handleAddClick,
    getItemLabel,
    handleRemoveItem,
  };
};
